import { Grid, Menu, Tooltip } from "antd";
import { connect } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import utils from "utils";
import navigationConfig from "../../configs/NavigationConfig";
import { NAV_TYPE_SIDE } from "../../constants/ThemeConstant";
import { onMobileNavToggle } from "../../redux/actions/Theme";
import { getResolvedUserType } from "../../utils/MyMUtils";
import { getNavEntryHref, isNavEntryActive, isTopLevelNavVisible } from "../../utils/navUtils";
import { AutoLoginLoaderAction } from "../../redux/actions/autoLoginLoader.action";
import { autoLogin } from "../../redux/actions/postAutoLogin.action";
import Icon from "../util-components/Icon";
import IntlMessage from "../util-components/IntlMessage";
import { ExternalLinkIcon } from "./NavIcons";
import { getApi } from "../../utils/apiCall";

// With PureVue on, Cleaning Excellence and Asepsis Loop open already signed in:
// a one-time code from PureVue (through the ERP backend) is passed to the module.
// The configured address may be a page (".../login.php", ".../auth/login"), so the
// sign-in page is found from it: CE's sits beside its PHP pages, Loop's at the site root.
const PUREVUE_ON = Boolean(process.env.REACT_APP_PUREVUE_URL);
const SIGNED_IN_LINKS = {
  "cleaning-excellence": { product: "CE", page: "purevue_sso.php?code=", from: "folder" },
  "asepsis-loop": { product: "LOOP", page: "auth/purevue?code=", from: "root" },
};

export function signedInUrl(configured, link, code) {
  const address = new URL(configured);
  const base = link.from === "root" ? address.origin + "/" : new URL(".", address).href;
  return base + link.page + encodeURIComponent(code);
}

const { SubMenu } = Menu;
const { useBreakpoint } = Grid;

const setLocale = (isLocaleOn, localeKey) => (isLocaleOn ? <IntlMessage id={localeKey} /> : localeKey.toString());

const SIDE_NAV_SECTIONS = [
  { key: "core", label: "Core" },
  { key: "operations", label: "Operations" },
  { key: "connected", label: "Connected Services" },
];

const SideNavContent = (props) => {
  const { localization, onMobileNavToggle, isSuperAdmin, collapsed } = props;
  const isMobile = !utils.getBreakPoint(useBreakpoint()).includes("lg");
  const { pathname } = useLocation();

  const closeMobileNav = () => {
    if (isMobile) {
      onMobileNavToggle(false);
    }
  };

  const openExternalLink = (menu) => {
    closeMobileNav();
    const base = String(process.env[menu.urlEnvKey] || "").replace(/\/+$/, "");
    const signedIn = PUREVUE_ON && SIGNED_IN_LINKS[menu.key];
    if (!signedIn) {
      window.open(base, "_blank", "noopener,noreferrer");
      return;
    }
    // opened straight away so the browser does not block it, then sent on
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    getApi(`/purevue_handover/${signedIn.product}`).then(({ error, result }) => {
      if (!error && result?.success && result.result?.code) {
        const url = signedInUrl(base, signedIn, result.result.code);
        if (tab) tab.location.href = url;
        else window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      const reason = result?.message || "";
      // a client not managed from PureVue: the module's own sign-in, as before
      if (!reason || /not managed from PureVue|not switched on/i.test(reason)) {
        if (tab) tab.location.href = base;
        else window.open(base, "_blank", "noopener,noreferrer");
        return;
      }
      if (tab) tab.close();
      toast.warning(reason);
    });
  };

  // Collapsed sidebar shows icons only; the label moves into a tooltip.
  const withTooltip = (menu, item) =>
    collapsed && !isMobile ? (
      <Tooltip key={menu.key} title={setLocale(localization, menu.title)} placement="right">
        {item}
      </Tooltip>
    ) : (
      item
    );

  const renderItem = (menu) => {
    const content = (
      <>
        {menu.icon ? <Icon type={menu.icon} /> : null}
        <span className="side-nav-item-label">{setLocale(localization, menu.title)}</span>
        {menu.simpleExternal ? <ExternalLinkIcon className="side-nav-external-icon" /> : null}
      </>
    );

    if (menu.simpleExternal) {
      return withTooltip(
        menu,
        <button key={menu.key} type="button" className="side-nav-item" aria-label={collapsed ? menu.title : undefined} onClick={() => openExternalLink(menu)}>
          {content}
        </button>
      );
    }

    const active = isNavEntryActive(menu, pathname);
    return withTooltip(
      menu,
      <Link
        key={menu.key}
        to={getNavEntryHref(menu)}
        className={`side-nav-item${active ? " active" : ""}`}
        aria-current={active ? "page" : undefined}
        aria-label={collapsed ? menu.title : undefined}
        onClick={closeMobileNav}
      >
        {content}
      </Link>
    );
  };

  const userType = getResolvedUserType();
  // External services only show once their URL is configured for this build.
  const visibleMenus = navigationConfig.filter(
    (menu) => isTopLevelNavVisible(menu, isSuperAdmin, userType) && !(menu.simpleExternal && !process.env[menu.urlEnvKey])
  );
  const sections = SIDE_NAV_SECTIONS.map((section) => ({
    ...section,
    items: visibleMenus.filter((menu) => (menu.section || "core") === section.key),
  })).filter((section) => section.items.length > 0);

  return (
    <nav className="side-nav-content">
      {sections.map((section, index) => (
        <div key={section.key} className="side-nav-section">
          {index > 0 ? <div className="side-nav-divider" /> : null}
          <div className="side-nav-section-label">{section.label}</div>
          {section.items.map(renderItem)}
        </div>
      ))}
    </nav>
  );
};

const TopNavContent = (props) => {
  const { topNavColor, localization, isSuperAdmin } = props;
  const location = useLocation();
  const dispatch = useDispatch();
  const currentUserType = getResolvedUserType();

  const SUPER_ADMIN_ONLY_KEYS = [
    "staff", "usermanagement", "hrusers", "facilityportal", "communication-management",
    "contactdetails", "businessinsurance", "certificates", "statements", "vehicle", "setting",
  ];

  const matchesUserType = (menuType) => {
    if (menuType == null) {
      return true;
    }

    if (Array.isArray(menuType)) {
      return menuType.includes(currentUserType) || menuType.includes(null);
    }

    return menuType === currentUserType;
  };

  const isMenuVisible = (menu) => {
    if (!matchesUserType(menu.type)) return false;
    if (SUPER_ADMIN_ONLY_KEYS.includes(menu.key) && !isSuperAdmin) return false;
    return true;
  };

  // Handle external link clicks - call autologin API and open in new tab
  const handleExternalClick = (e, menuKey) => {
    e.preventDefault();
    e.stopPropagation();
    
    const user_id = localStorage.getItem("user_id");
    let moduleId, urlEnvKey;
    
    // Map menu keys to module IDs and environment variable keys
    switch(menuKey) {
      case 'cleaning-audit':
        moduleId = "18";
        urlEnvKey = "REACT_APP_URL_CA";
        break;
      case 'laundry-audit':
        moduleId = "19";
        urlEnvKey = "REACT_APP_URL_LA";
        break;
      case 'employee-connect':
        moduleId = "20";
        urlEnvKey = "REACT_APP_URL_EDU";
        break;
      case 'work-scheduler':
        moduleId = "2";
        urlEnvKey = "REACT_APP_URL_PERIODICAL";
        break;
      default:
        return;
    }
    
    const post = {
      module_id: moduleId,
      user_id: user_id,
    };
    
    dispatch(AutoLoginLoaderAction({ start: true })).then(() => {
      dispatch(autoLogin(post))
        .then((record) => {
          if (record.payload.success == true) {
            const externalUrl = process.env[urlEnvKey];
            let autologinUrl;
            
            // Build the autologin URL based on the module
            if (menuKey === 'cleaning-audit') {
              const token = record.payload.result.token_id;
              autologinUrl = `${externalUrl}/autologin?token=${token}&clientId=${localStorage.getItem("client_id")}`;
            } else if (menuKey === 'laundry-audit') {
              const { luser_id, token_id, user_id } = record.payload.result;
              autologinUrl = `${externalUrl}/autologin?userid=${user_id}&luserid=${luser_id}&token=${token_id}&client_id=${localStorage.getItem("client_id")}`;
            } else if (menuKey === 'employee-connect') {
              const token = record.payload.result[0].token_id;
              const userid = record.payload.result[0].user_id;
              autologinUrl = `${externalUrl}/autologin?token=${token}&userId=${userid}&clientId=${localStorage.getItem("client_id")}`;
            } else if (menuKey === 'work-scheduler') {
              const token = record.payload.result.token_id;
              const userid = record.payload.result.user_id;
              autologinUrl = `${externalUrl}/autologin?token=${token}&userid=${userid}&clientId=${localStorage.getItem("client_id")}`;
            }
            
            window.open(autologinUrl, '_blank', 'noopener,noreferrer');
          } else {
            toast.error(record.payload.message);
          }
        })
        .finally(() => {
          dispatch(AutoLoginLoaderAction({ stop: true }));
        });
    });
  };

  // Function to get selected keys including parent submenu
  const getSelectedKeys = () => {
    const currentPath = location.pathname;
    const selectedKeys = [];
    
    // Check if current path is in any submenu and add both parent and child keys
    navigationConfig.forEach((menu) => {
      if (menu.submenu && menu.submenu.length > 0) {
        menu.submenu.forEach((subMenu) => {
          if (subMenu.path === currentPath) {
            // Add both the submenu key and parent menu key
            selectedKeys.push(subMenu.key);
            selectedKeys.push(menu.key);
          }
        });
      } else if (menu.path === currentPath) {
        // For direct menu items without submenu
        selectedKeys.push(menu.key);
      }
    });
    
    // If no match found, try to match by path directly (fallback)
    if (selectedKeys.length === 0) {
      selectedKeys.push(currentPath);
    }
    
    console.log('Current Path:', currentPath);
    console.log('Selected Keys:', selectedKeys);
    
    return selectedKeys;
  };

  return (
    <Menu mode="horizontal" style={{ backgroundColor: "#2f6fe000" }} selectedKeys={getSelectedKeys()}>
      {navigationConfig.map((menu) =>
        isMenuVisible(menu) ? (
          menu.submenu.length > 0 ? (
            <SubMenu
              key={menu.key}
              popupClassName="top-nav-menu"
              title={
                <span>
                  {menu.icon ? <Icon type={menu?.icon} /> : null}
                  <span>{setLocale(localization, menu.title)}</span>
                </span>
              }
            >
              {menu.submenu.map((subMenuFirst) =>
                subMenuFirst.submenu.length > 0 ? (
                  <SubMenu key={subMenuFirst.key} icon={subMenuFirst.icon ? <Icon type={subMenuFirst?.icon} /> : null} title={setLocale(localization, subMenuFirst.title)}>
                    {subMenuFirst.submenu.map((subMenuSecond) => (
                      <Menu.Item key={subMenuSecond.key}>
                        <span>{setLocale(localization, subMenuSecond.title)}</span>
                        {subMenuSecond.external ? (
                          <a 
                            href="#" 
                            onClick={(e) => handleExternalClick(e, subMenuSecond.key)}
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                          />
                        ) : (
                          <Link to={subMenuSecond.path} />
                        )}
                      </Menu.Item>
                    ))}
                  </SubMenu>
                ) : (
                  <Menu.Item key={subMenuFirst.key}>
                    {subMenuFirst.icon ? <Icon type={subMenuFirst?.icon} /> : null}
                    <span>{setLocale(localization, subMenuFirst.title)}</span>
                    {subMenuFirst.external ? (
                      <a 
                        href="#" 
                        onClick={(e) => handleExternalClick(e, subMenuFirst.key)}
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                      />
                    ) : (
                      <Link to={subMenuFirst.path} />
                    )}
                  </Menu.Item>
                )
              )}
            </SubMenu>
          ) : (
            <Menu.Item
              key={menu.path}
              style={{
                backgroundColor: "transparent",
              }}
              className={`${menu.type == "H" || menu.type == "U" || menu.type == "F" || menu.type == "T" || menu.type == "C" ? "custom_top_nav" : ""}  `}
            >
              {menu.icon ? <Icon type={menu?.icon} /> : null}
              <span>{setLocale(localization, menu?.title)}</span>
              {menu.path && (menu.type != "H" || menu.type != "U" || menu.type != "F" || menu.type != "T" || menu.type != "C") ? (
                menu.external ? (
                  <a 
                    href="#" 
                    onClick={(e) => handleExternalClick(e, menu.key)}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                  />
                ) : (
                  <Link to={menu.path} />
                )
              ) : null}
            </Menu.Item>
          )
        ) : null
      )}
    </Menu>
  );
};

const MenuContent = (props) => {
  return props.type === NAV_TYPE_SIDE ? <SideNavContent {...props} /> : <TopNavContent {...props} />;
};

const mapStateToProps = ({ theme, userAuth }) => {
  const { sideNavTheme, topNavColor } = theme;
  const isSuperAdmin = userAuth?.isSuperAdmin == 1; // loose equality handles both string "1" and number 1
  return { sideNavTheme, topNavColor, isSuperAdmin };
};

export default connect(mapStateToProps, { onMobileNavToggle })(MenuContent);
